import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Users, Calendar, Clock, Star, Mail, Phone, Globe, Share2, Activity, ChevronRight, Pencil, Check, MessageCircle } from 'lucide-react';
import RatingDisplay from './RatingDisplay';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/apiService';
import { toast } from 'sonner';
import RunCard from './RunCard';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './dialog';
import { formatPhoneNumberDisplay } from '../../utils/phoneUtils';
import { formatDate, formatTime } from '../../utils/dataTransformers';
import { getAllImageUrls, convertHeicToWebFormat } from '../../utils/imageUtils';
import { Carousel, CarouselContent, CarouselItem } from './carousel';
import { parseMemberResponse, getMemberId, formatMemberJoinDate } from '../../utils/memberUtils';
import { parseInstagramProfile } from '../../utils/instagramUtils';

const ScrollableClubCard = ({ 
  club, 
  onClick, 
  showHover = true,
  showType = true,
  showConnections = true,
  showLocation = true,
  showNextRun = true,
  showRating = true,
  isFullProfile = false,
  onConnectionChange
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionLoading, setConnectionLoading] = useState(false);
  const [clubConnections, setClubConnections] = useState([]);
  const [showRunCard, setShowRunCard] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [loadingRunCard, setLoadingRunCard] = useState(false);
  const [nextRun, setNextRun] = useState(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageError, setImageError] = useState(false);
  const [carouselApi, setCarouselApi] = useState(null);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const memberClickInProgress = useRef(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Check if current user is the club owner
  const isClubOwner = user && user.user_type === 'runclub' && user.name === club.organizer_name;
  
  // Constants
  const DESCRIPTION_CHAR_LIMIT = 150;
  const DEFAULT_DESCRIPTION = 'Join our running community!';
  const instagramProfile = parseInstagramProfile(club?.instagram);

  const handleClick = () => {
    if (onClick) {
      onClick(club);
    }
  };

  // Check connection status for runners
  useEffect(() => {
    const checkConnectionStatus = async () => {
      if (user && user.user_type === 'runner' && !isClubOwner && isFullProfile) {
        try {
          setConnectionLoading(true);
          const response = await apiService.clubs.getConnectionStatus(club.id);
          setIsConnected(response.is_connected);
        } catch (error) {
          console.error('Failed to check connection status:', error);
        } finally {
          setConnectionLoading(false);
        }
      }
    };

    checkConnectionStatus();
  }, [user, club.id, isClubOwner, isFullProfile]);

  const handleConnectionToggle = async () => {
    if (!user || user.user_type !== 'runner') return;
    
    try {
      setConnectionLoading(true);
      if (isConnected) {
        await apiService.clubs.disconnectClub(club.id);
        setIsConnected(false);
        toast.success('Disconnected from club successfully!');
        // Notify parent component of connection change
        if (onConnectionChange) {
          onConnectionChange(club.id, false);
        }
      } else {
        await apiService.clubs.connectClub(club.id);
        setIsConnected(true);
        toast.success('Connected to club successfully!');
        // Notify parent component of connection change
        if (onConnectionChange) {
          onConnectionChange(club.id, true);
        }
      }
    } catch (error) {
      // Show specific error message from backend if available
      const errorMessage = error.response?.data?.detail || 'Failed to update connection status';
      toast.error(errorMessage);
      console.error('Connection error:', error);
    } finally {
      setConnectionLoading(false);
    }
  };

  // Fetch club members
  const fetchClubConnections = useCallback(async () => {
    if (club.id && isFullProfile) {
      try {
        setLoadingConnections(true);
        const response = await apiService.clubs.getClubMembers(club.id, 200, 0);
        // Extract members array from response using shared utility
        const members = parseMemberResponse(response);
        setClubConnections(members);
      } catch (error) {
        console.error('Failed to fetch club members:', error);
        setClubConnections([]);
      } finally {
        setLoadingConnections(false);
      }
    }
  }, [club.id, isFullProfile]);

  useEffect(() => {
    fetchClubConnections();
  }, [fetchClubConnections]);

  // Fetch next upcoming run for the club
  const fetchNextRun = useCallback(async () => {
    if (!club?.id || !isFullProfile) return;
    
    try {
      const meetings = await apiService.meetings.getAll({ club_id: club.id, limit: 100 });
      
      // Filter and sort upcoming meetings
      const now = new Date();
      const upcoming = (meetings || [])
        .filter(meeting => {
          if (!meeting.scheduled_date) return false;
          if (meeting.status === 'cancelled') return false;
          const meetingDate = new Date(meeting.scheduled_date);
          return meetingDate > now;
        })
        .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date));
      
      setNextRun(upcoming.length > 0 ? upcoming[0] : null);
    } catch (error) {
      console.error('Failed to fetch next run:', error);
      setNextRun(null);
    }
  }, [club?.id, isFullProfile]);

  useEffect(() => {
    fetchNextRun();
  }, [fetchNextRun]);

  // Memoized description logic
  const descriptionLogic = React.useMemo(() => {
    try {
      const description = club?.description || DEFAULT_DESCRIPTION;
      const shouldTruncate = description.length > DESCRIPTION_CHAR_LIMIT;
      const displayText = shouldTruncate && !isDescriptionExpanded 
        ? description.substring(0, DESCRIPTION_CHAR_LIMIT) + '...'
        : description;
      
      return {
        displayText,
        shouldTruncate,
        isExpanded: isDescriptionExpanded
      };
    } catch (error) {
      console.error('Error processing description:', error);
      return {
        displayText: DEFAULT_DESCRIPTION,
        shouldTruncate: false,
        isExpanded: false
      };
    }
  }, [club?.description, isDescriptionExpanded]);

  // Handle connection click to show run card
  const handleConnectionClick = async (connection) => {
    setLoadingRunCard(true);
    setSelectedConnection(connection);
    setShowRunCard(true);
    setLoadingRunCard(false);
  };

  // Handle member click to view runcard
  const handleMemberClick = async (member) => {
    if (!member) {
      console.error('handleMemberClick: member is null or undefined');
      return;
    }

    // Prevent multiple simultaneous clicks
    if (memberClickInProgress.current) {
      return;
    }

    // Close members modal first
    setShowMembersModal(false);
    
    // Extract member ID using utility function
    const memberId = getMemberId(member);
    
    if (!memberId) {
      toast.error('Unable to load member profile: missing member ID');
      console.error('handleMemberClick: member ID not found', member);
      return;
    }

    memberClickInProgress.current = true;

    try {
      setLoadingRunCard(true);
      // Fetch member's full profile
      const memberProfile = await apiService.users.getProfile(memberId);
      
      if (!memberProfile) {
        throw new Error('Profile data is null or undefined');
      }
      
      setSelectedConnection(memberProfile);
      setShowRunCard(true);
    } catch (error) {
      console.error('Failed to fetch member profile:', error);
      toast.error('Failed to load member profile. Please try again.');
      // Don't show runcard if we can't fetch the profile
      // The member data from the list might not have all required fields
    } finally {
      setLoadingRunCard(false);
      memberClickInProgress.current = false;
    }
  };

  // Helper function to capitalize words
  const capitalizeWords = (str) => {
    if (!str) return '';
    return str.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  };

  // Get all image URLs from clubcard_images array, or fall back to single image
  const imageUrls = React.useMemo(() => {
    if (club.clubcard_images && Array.isArray(club.clubcard_images) && club.clubcard_images.length > 0) {
      return getAllImageUrls(club.clubcard_images);
    }
    if (club.runcard_image) {
      return [convertHeicToWebFormat(club.runcard_image)];
    }
    return [];
  }, [club.clubcard_images, club.runcard_image]);

  // Get primary image URL for single image display
  const primaryImageUrl = React.useMemo(() => {
    if (imageUrls.length > 0) {
      return imageUrls[0];
    }
    return null;
  }, [imageUrls]);

  return (
    <div
      className={`rounded-3xl overflow-hidden shadow-2xl border border-white/20 relative w-[400px] bg-runcards-background ${
        showHover ? 'hover:scale-105 transition-all duration-300 hover:shadow-3xl' : ''
      } ${onClick ? 'cursor-pointer' : ''}`}
      onClick={handleClick}
    >
      {/* Scrollable Content */}
      <div 
        className={`${isFullProfile ? 'max-h-[80vh] overflow-y-auto scrollbar-hide' : ''}`}
      >
        {/* Image Section */}
        <div className="relative h-[500px] overflow-hidden rounded-t-3xl">
          {imageUrls.length > 1 ? (
            // Multiple images - use carousel
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              className="w-full h-full"
              setApi={(api) => {
                if (api) {
                  setCarouselApi(api);
                  // Set initial index
                  setCurrentImageIndex(api.selectedScrollSnap());
                  // Listen for changes
                  api.on("select", () => {
                    setCurrentImageIndex(api.selectedScrollSnap());
                  });
                }
              }}
            >
              <CarouselContent className="h-full -ml-0">
                {imageUrls.map((url, index) => (
                  <CarouselItem key={index} className="h-full pl-0 basis-full">
                    <div className="relative w-full h-full">
                      <img 
                        src={url} 
                        alt={`${club.name} - Image ${index + 1}`}
                        className="w-full h-full object-cover"
                        style={{
                          objectPosition: club.imagePosition ? `${club.imagePosition.x}% ${club.imagePosition.y}%` : 'center center'
                        }}
                        onError={(e) => {
                          console.error('Failed to load club image:', url);
                          setImageError(true);
                          e.target.style.display = 'none';
                        }}
                        onLoad={() => {
                          setImageError(false);
                        }}
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          ) : primaryImageUrl && !imageError ? (
            // Single image
            <img 
              src={primaryImageUrl} 
              alt={club.name}
              className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
              style={{
                objectPosition: club.imagePosition ? `${club.imagePosition.x}% ${club.imagePosition.y}%` : 'center center'
              }}
              onError={(e) => {
                console.error('Failed to load club image:', primaryImageUrl);
                setImageError(true);
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'flex';
              }}
              onLoad={() => {
                setImageError(false);
              }}
            />
          ) : null}
          <div className={`w-full h-full bg-runcards-background ${primaryImageUrl && !imageError ? 'spray-paint-placeholder' : 'spray-paint-placeholder'}`} style={{display: primaryImageUrl && !imageError ? 'none' : 'flex'}}>
          </div>
          
          {/* Gradient overlay for better text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
          
          {/* Club Type Badge - Top Left */}
          {showType && club.type && (
            <div className="absolute top-4 left-4 z-10">
              <span className="bg-black/60 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-medium border border-white/20">
                {club.type}
              </span>
            </div>
          )}

          {/* Share Button - Top Right (only if not club owner) */}
          {!isClubOwner && isFullProfile && (
            <div className="absolute top-4 right-4 z-10">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (navigator.share) {
                    navigator.share({
                      title: `${club.name} - Running Club`,
                      text: `Check out ${club.name} running club! ${club.description || 'Join us for great runs and community.'}`,
                      url: window.location.href
                    }).catch(() => {});
                  }
                }}
                className="w-8 h-8 rounded-full bg-black/60 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-black/80 transition-colors"
              >
                <Share2 className="w-4 h-4 text-white" />
              </button>
            </div>
          )}

          {/* Dots Indicator for multiple images */}
          {imageUrls.length > 1 && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
              {imageUrls.map((_, index) => (
                <button
                  key={index}
                  className={`h-2 rounded-full transition-all ${
                    index === currentImageIndex 
                      ? 'bg-white w-6' 
                      : 'bg-white/50 hover:bg-white/75 w-2'
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (carouselApi) {
                      carouselApi.scrollTo(index);
                    }
                  }}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Information Section - Dark background like mobile */}
        <div className="bg-runcards-background">
          {/* Basic Info Section - Title with Connect button, Location/Type, Rating/Connections */}
          <div className="p-4">
            {/* Title with Connect Button */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2 flex-1 mr-3">
                <h3 className="text-2xl font-bold text-runcards-text-primary font-runcards-bold" style={{letterSpacing: '-0.5px'}}>
                  {club.name}
                </h3>
                {isFullProfile && isClubOwner && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Navigate to edit page or show edit modal
                      window.location.href = '/profile';
                    }}
                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors border border-gray-200"
                    aria-label="Edit clubcard"
                  >
                    <Pencil className="w-4 h-4 text-gray-700" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {isFullProfile && (isConnected || isClubOwner) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/messages/club/${club.id}`);
                    }}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 bg-transparent border border-runcards-accent text-runcards-accent hover:bg-runcards-accent/10"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    Group chat
                  </button>
                )}
                {isFullProfile && user && user.user_type === 'runner' && !isClubOwner && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleConnectionToggle();
                    }}
                    disabled={connectionLoading}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                      isConnected 
                        ? 'bg-transparent border border-runcards-accent text-runcards-accent hover:bg-runcards-accent/10' 
                        : 'bg-gradient-to-r from-runcards-accent to-[#8a91a8] text-white hover:opacity-90'
                    } ${connectionLoading ? 'opacity-60' : ''}`}
                    style={{fontFamily: 'Montserrat, sans-serif'}}
                  >
                    {connectionLoading ? '...' : isConnected ? 'Connected' : 'Connect'}
                  </button>
                )}
              </div>
            </div>

            {/* First Row: Location and Club Type */}
            {(club.city || club.type) && (
              <div className="flex items-center gap-2 mb-3">
                {club.city && (
                  <>
                    <div className="flex items-center text-runcards-text-primary">
                      <MapPin className="w-3.5 h-3.5 mr-1.5 text-runcards-text-secondary" />
                      <span className="text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        {club.city}
                      </span>
                    </div>
                    {club.type && <span className="text-runcards-text-primary">•</span>}
                  </>
                )}
                {club.type && (
                  <div className="flex items-center text-runcards-text-primary">
                    <Activity className="w-3.5 h-3.5 mr-1.5 text-runcards-text-secondary" />
                    <span className="text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      {capitalizeWords(club.type)}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Second Row: Reviews, Members, Instagram */}
            {(club.rating !== undefined || club.members !== undefined || instagramProfile) && (
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {club.rating !== undefined && (
                  <>
                    <div className="flex items-center text-runcards-text-primary">
                      <Star className="w-3.5 h-3.5 mr-1.5 text-runcards-text-secondary fill-yellow-400 text-yellow-400" />
                      <span className="text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        {club.rating.toFixed(1)} ({club.total_ratings || 0} {(club.total_ratings || 0) === 1 ? 'review' : 'reviews'})
                      </span>
                    </div>
                    {(club.members !== undefined || instagramProfile) && (
                      <span className="text-runcards-text-primary">•</span>
                    )}
                  </>
                )}
                {club.members !== undefined && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Ensure members are fetched before showing modal
                      if (clubConnections.length === 0 && !loadingConnections && club?.id) {
                        fetchClubConnections();
                      }
                      setShowMembersModal(true);
                    }}
                    className="flex items-center text-runcards-text-primary cursor-pointer hover:opacity-80 transition-opacity"
                    aria-label={`View ${club.members || 0} ${(club.members || 0) === 1 ? 'member' : 'members'}`}
                  >
                    <Users className="w-3.5 h-3.5 mr-1.5 text-runcards-text-secondary" />
                    <span className="text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      {club.members || 0} {(club.members || 0) === 1 ? 'member' : 'members'}
                    </span>
                  </button>
                )}
                {instagramProfile && club.members !== undefined && (
                  <span className="text-runcards-text-primary">•</span>
                )}
                {instagramProfile && (
                  <a
                    href={instagramProfile.profileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="flex items-center text-runcards-text-primary cursor-pointer hover:opacity-80 transition-opacity"
                    aria-label={`Open Instagram profile @${instagramProfile.handle}`}
                  >
                    <span className="text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      @{instagramProfile.handle}
                    </span>
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Next Run Section */}
          {isFullProfile && (
            <div className="px-4 py-6 border-t border-white/10">
              <h4 className="text-lg font-bold text-runcards-text-primary mb-4" style={{fontFamily: 'Montserrat, sans-serif'}}>
                Next Run: {nextRun?.name ? capitalizeWords(nextRun.name) : 'No upcoming runs'}
              </h4>
              
              {nextRun && (
                <div className="space-y-3">
                  {/* Date & Time */}
                  {nextRun.scheduled_date && (
                    <div className="flex items-center text-runcards-text-secondary">
                      <Calendar className="w-4 h-4 mr-2 text-runcards-text-secondary" />
                      <span className="text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        {formatDate(nextRun.scheduled_date, { format: 'short', timezone: nextRun.timezone || null })} at {formatTime(nextRun.scheduled_date, nextRun.timezone || null)}
                      </span>
                    </div>
                  )}
                  
                  {/* Location */}
                  {nextRun.location && (
                    <div className="flex items-center text-runcards-text-secondary">
                      <MapPin className="w-4 h-4 mr-2 text-runcards-text-secondary" />
                      <span className="text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        {nextRun.location}
                      </span>
                    </div>
                  )}
                  
                  {/* Distance & Pace */}
                  {(nextRun.distance || nextRun.pace) && (
                    <div className="flex items-center text-runcards-text-secondary">
                      <Clock className="w-4 h-4 mr-2 text-runcards-text-secondary" />
                      <span className="text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        {nextRun.distance || ''} {nextRun.distance && nextRun.pace ? 'at ' : ''}{nextRun.pace || ''}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* About Section */}
          {isFullProfile && (
            <div className="px-4 py-6 border-t border-white/10">
              <h4 className="text-lg font-bold text-runcards-text-primary mb-3" style={{fontFamily: 'Montserrat, sans-serif'}}>
                About us
              </h4>
              <p className="text-sm text-runcards-text-secondary leading-relaxed" style={{fontFamily: 'Montserrat, sans-serif'}}>
                {descriptionLogic.displayText}
                {descriptionLogic.shouldTruncate && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDescriptionExpanded(!isDescriptionExpanded);
                    }}
                    className="ml-1 text-runcards-accent hover:text-runcards-accent-hover font-semibold"
                  >
                    {isDescriptionExpanded ? ' Show Less' : ' Show More'}
                  </button>
                )}
              </p>
            </div>
          )}

          {/* Meet Up Spot Section */}
          {isFullProfile && club?.home_base_address && typeof club.home_base_address === 'string' && club.home_base_address.trim() !== '' && (
            <div className="px-4 py-6 border-t border-white/10">
              <h4 className="text-lg font-semibold text-runcards-text-primary mb-3" style={{fontFamily: 'Montserrat, sans-serif'}}>
                Meet Up Spot
              </h4>
              <div className="flex items-start mb-3">
                <MapPin className="w-5 h-5 mr-2 text-runcards-text-primary mt-0.5 flex-shrink-0" />
                <span className="text-base font-medium text-runcards-text-primary leading-relaxed" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  {club.home_base_address}
                </span>
              </div>
              {club?.home_base_description && typeof club.home_base_description === 'string' && club.home_base_description.trim() !== '' && (
                <p className="text-base text-runcards-text-primary leading-relaxed mt-2 opacity-85" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  {club.home_base_description}
                </p>
              )}
            </div>
          )}

          {/* Connections Section */}
          {isFullProfile && (
            <div className="px-4 py-6 border-t border-white/10">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-bold text-runcards-text-primary" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  Connections ({clubConnections.length})
                </h4>
              </div>
              {loadingConnections ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-runcards-text-secondary text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
                    Loading connections...
                  </div>
                </div>
              ) : clubConnections.length > 0 ? (
                <div className="overflow-x-auto scrollbar-hide">
                  <div className="flex gap-4 pb-2">
                    {clubConnections.map((connection, index) => (
                      <button
                        key={connection.user_id || index}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleConnectionClick(connection);
                        }}
                        className="flex flex-col items-center flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                      >
                        <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white/20 mb-2">
                          {(connection.profile_picture || connection.user_profile_picture) ? (
                            <img 
                              src={connection.profile_picture || connection.user_profile_picture} 
                              alt={connection.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-white/10 flex items-center justify-center">
                              <span className="text-xl text-runcards-text-primary font-bold">
                                {(connection.name || 'A').charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="text-center max-w-[80px]">
                          {(() => {
                            const fullName = connection.name || 'Anonymous';
                            const nameParts = fullName.trim().split(/\s+/);
                            const firstName = nameParts[0] || '';
                            const lastName = nameParts.slice(1).join(' ') || '';
                            
                            return (
                              <>
                                <span className="text-xs text-runcards-text-primary block truncate" style={{fontFamily: 'Montserrat, sans-serif'}}>
                                  {firstName}
                                </span>
                                {lastName && (
                                  <span className="text-xs text-runcards-text-primary block truncate" style={{fontFamily: 'Montserrat, sans-serif'}}>
                                    {lastName}
                                  </span>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-runcards-text-secondary text-center py-4" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  No connections yet
                </p>
              )}
            </div>
          )}

          {/* Two Column Layout: Club Details and Contact */}
          {isFullProfile && (
            <div className="px-4 py-6 border-t border-white/10 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column: Club Details */}
              <div>
                <h4 className="text-lg font-bold text-runcards-text-primary mb-3" style={{fontFamily: 'Montserrat, sans-serif'}}>
                  Club Details
                </h4>
                <div className="space-y-2">
                  {club.organizer_name && (
                    <div className="flex items-start">
                      <span className="text-runcards-text-secondary mr-2">•</span>
                      <span className="text-sm text-runcards-text-secondary" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        Organizer: {club.organizer_name}
                      </span>
                    </div>
                  )}
                  
                  {club.skill_level && (
                    <div className="flex items-start">
                      <span className="text-runcards-text-secondary mr-2">•</span>
                      <span className="text-sm text-runcards-text-secondary" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        Who is this club for?: {capitalizeWords(club.skill_level)}
                      </span>
                    </div>
                  )}
                  
                  {club.rating !== undefined && (
                    <div className="flex items-start">
                      <span className="text-runcards-text-secondary mr-2">•</span>
                      <span className="text-sm text-runcards-text-secondary" style={{fontFamily: 'Montserrat, sans-serif'}}>
                        Rating: {club.rating.toFixed(1)} ({club.total_ratings || 0} {(club.total_ratings || 0) === 1 ? 'review' : 'reviews'})
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column: Contact Information */}
              {(club.contact_email || club.contact_phone || club.website) && (
                <div>
                  <h4 className="text-lg font-bold text-runcards-text-primary mb-3" style={{fontFamily: 'Montserrat, sans-serif'}}>
                    Contact
                  </h4>
                  <div className="space-y-2">
                    {club.contact_email && (
                      <a
                        href={`mailto:${club.contact_email}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center text-sm text-runcards-text-secondary hover:text-runcards-accent transition-colors"
                        style={{fontFamily: 'Montserrat, sans-serif'}}
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        {club.contact_email}
                      </a>
                    )}
                    
                    {club.contact_phone && (
                      <a
                        href={`tel:${club.contact_phone.replace(/\D/g, '')}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center text-sm text-runcards-text-secondary hover:text-runcards-accent transition-colors"
                        style={{fontFamily: 'Montserrat, sans-serif'}}
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        {formatPhoneNumberDisplay(club.contact_phone)}
                      </a>
                    )}
                    
                    {club.website && (
                      <a
                        href={club.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center text-sm text-runcards-text-secondary hover:text-runcards-accent transition-colors"
                        style={{fontFamily: 'Montserrat, sans-serif'}}
                      >
                        <Globe className="w-4 h-4 mr-2" />
                        {club.website}
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons Section - Fixed at bottom */}
          {isFullProfile && (
            <div className="px-4 py-6 border-t border-white/10 flex gap-3">
              {club?.id && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Navigate to club runs - this would need to be handled by parent
                    if (onClick) onClick(club);
                  }}
                  className="flex-1 bg-runcards-accent text-white px-4 py-3 rounded-lg font-semibold text-sm hover:bg-runcards-accent-hover transition-colors"
                  style={{fontFamily: 'Montserrat, sans-serif'}}
                >
                  View Runs
                </button>
              )}

              {user && user.user_type === 'runner' && !isClubOwner && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleConnectionToggle();
                  }}
                  disabled={connectionLoading}
                  className={`flex-1 px-4 py-3 rounded-lg font-semibold text-sm transition-colors ${
                    isConnected 
                      ? 'bg-transparent border border-runcards-accent text-runcards-accent hover:bg-runcards-accent/10' 
                      : 'bg-gradient-to-r from-runcards-accent to-[#8a91a8] text-white hover:opacity-90'
                  } ${connectionLoading ? 'opacity-60' : ''}`}
                  style={{fontFamily: 'Montserrat, sans-serif'}}
                >
                  {connectionLoading ? '...' : isConnected ? 'Disconnect' : 'Connect'}
                </button>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Connection Run Card Modal */}
      <Dialog open={showRunCard} onOpenChange={setShowRunCard}>
        <DialogContent className="bg-transparent border-none shadow-none max-w-fit p-0">
          {/* Hidden accessibility elements */}
          <DialogHeader className="sr-only">
            <DialogTitle>Run Card</DialogTitle>
            <DialogDescription>
              {selectedConnection?.name ? `Run card for ${selectedConnection.name}` : 'Loading run card'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="relative">
            <div className="flex justify-center">
              {loadingRunCard ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-white/60" style={{fontFamily: 'Montserrat, sans-serif'}}>
                    Loading run card...
                  </div>
                </div>
              ) : selectedConnection ? (
                <RunCard 
                  runner={selectedConnection}
                  showHover={false}
                  buttonText="View Profile"
                  onButtonClick={() => {
                    toast.info('Profile details coming soon');
                  }}
                />
              ) : (
                <div className="text-center py-8">
                  <div className="text-white/60" style={{fontFamily: 'Montserrat, sans-serif'}}>
                    Unable to load run card
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Members Modal */}
      <Dialog open={showMembersModal} onOpenChange={setShowMembersModal}>
        <DialogContent className="bg-runcards-background border border-white/20 shadow-2xl max-w-md max-h-[80vh] overflow-hidden flex flex-col">
          {/* Hidden accessibility elements */}
          <DialogHeader className="sr-only">
            <DialogTitle>Club Members</DialogTitle>
            <DialogDescription>
              List of members for {club?.name || 'this club'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/10">
              <h3 className="text-xl font-bold text-runcards-text-primary" style={{fontFamily: 'Montserrat, sans-serif'}}>
                Members ({club.members || 0})
              </h3>
            </div>

            {/* Members List */}
            <div className="flex-1 overflow-y-auto scrollbar-hide">
              {loadingConnections ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-runcards-accent mx-auto mb-4"></div>
                    <div className="text-runcards-text-secondary text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
                      Loading members...
                    </div>
                  </div>
                </div>
              ) : clubConnections.length > 0 ? (
                <div className="space-y-2">
                  {clubConnections.map((member, index) => {
                    // Use utility functions for consistent data extraction
                    const memberId = getMemberId(member);
                    const memberName = member?.name || 'Unknown Member';
                    const memberPicture = member?.profile_picture || member?.user_profile_picture;
                    const joinDate = formatMemberJoinDate(member?.connected_at);
                    
                    // Create a stable key - prefer memberId, fallback to index with member name
                    const itemKey = memberId ? `member-${memberId}` : `member-${index}-${memberName}`;

                    return (
                      <button
                        key={itemKey}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMemberClick(member);
                        }}
                        className="w-full flex items-center justify-between p-3 bg-white/5 rounded-lg border border-runcards-border/10 hover:bg-white/10 transition-colors text-left focus:outline-none focus:ring-2 focus:ring-runcards-accent focus:ring-offset-2 focus:ring-offset-runcards-background"
                        aria-label={`View ${memberName}'s runcard`}
                      >
                        <div className="flex items-center flex-1 min-w-0">
                          {/* Member Profile Picture */}
                          {memberPicture ? (
                            <div className="w-12 h-12 rounded-lg overflow-hidden mr-3 flex-shrink-0 relative">
                              <img 
                                src={memberPicture} 
                                alt={`${memberName}'s profile`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  // Hide image on error and show fallback
                                  e.target.style.display = 'none';
                                  const parent = e.target.parentElement;
                                  if (parent) {
                                    const fallback = parent.querySelector('.member-initial-fallback');
                                    if (fallback) {
                                      fallback.style.display = 'flex';
                                    }
                                  }
                                }}
                              />
                              <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center member-initial-fallback absolute inset-0" style={{display: 'none'}}>
                                <span className="text-lg text-runcards-text-primary font-bold">
                                  {memberName.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center mr-3 flex-shrink-0">
                              <span className="text-lg text-runcards-text-primary font-bold">
                                {memberName.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          
                          {/* Member Info */}
                          <div className="flex-1 min-w-0">
                            <div className="text-runcards-text-primary font-semibold truncate" style={{fontFamily: 'Montserrat, sans-serif'}}>
                              {memberName}
                            </div>
                            {joinDate && (
                              <div className="text-runcards-text-secondary text-xs" style={{fontFamily: 'Montserrat, sans-serif'}}>
                                Joined {joinDate}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Arrow indicator */}
                        <div className="ml-2 flex-shrink-0" aria-hidden="true">
                          <ChevronRight className="w-5 h-5 text-runcards-text-secondary" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-runcards-text-primary/40 mx-auto mb-4" />
                  <div className="text-runcards-text-primary/60 font-semibold mb-2" style={{fontFamily: 'Montserrat, sans-serif'}}>
                    No members yet
                  </div>
                  <div className="text-runcards-text-secondary text-sm" style={{fontFamily: 'Montserrat, sans-serif'}}>
                    Members will appear here when they join this club
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScrollableClubCard;
